"use client";

import { useCallback, useEffect, useReducer, useRef } from "react";

import type { AppLocale } from "@/i18n/locales";
import type { ThreatReportSummary } from "@/shared/cors-edge-lambda-api";
import {
  abandonLanguageSwitchLookup,
  evaluateLanguageSwitch,
  finishLanguageSwitchLookup,
  initialLanguageSwitchState,
  languageSwitchView,
  recordLanguageSwitchPick,
  type LanguageSwitchInput,
  type LanguageSwitchState,
  type LoadedThreatReport,
  type ReportIdentity,
} from "@/shared/threat-report-language-switch";

/**
 * Open the completed report generated in the UI language whenever a report
 * body is shown for a case or the language changes. When none exists, ask the
 * analyst to generate one. A version the analyst picked is left alone until
 * the case or language changes.
 */
export function useThreatReportLanguageSwitch(args: {
  locale: AppLocale;
  caseId?: string;
  enabled: boolean;
  blocked?: boolean;
  /** Set together with the report body, and null whenever no completed body is shown. */
  loadedReport: LoadedThreatReport | null;
  /** A new object each time the analyst picks a version. The value on mount is not a pick. */
  pickedVersion?: ReportIdentity | null;
  /** Rejects when the version list cannot be loaded. */
  lookupReports(): Promise<readonly ThreatReportSummary[]>;
  onOpen: (report: ThreatReportSummary) => void;
}): { promptOpen: boolean; isSwitching: boolean; dismissPrompt: () => void } {
  const input: LanguageSwitchInput = {
    locale: args.locale,
    caseId: args.caseId?.trim() || undefined,
    enabled: args.enabled,
    blocked: Boolean(args.blocked),
    loaded: args.loadedReport,
  };
  const stateRef = useRef<LanguageSwitchState>(initialLanguageSwitchState);
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const inputRef = useRef(input);
  const lookupRef = useRef(args.lookupReports);
  const onOpenRef = useRef(args.onOpen);
  const seenPickRef = useRef(args.pickedVersion);
  const mountedRef = useRef(false);
  inputRef.current = input;
  lookupRef.current = args.lookupReports;
  onOpenRef.current = args.onOpen;

  const commit = useCallback((next: LanguageSwitchState) => {
    if (next === stateRef.current) return;
    stateRef.current = next;
    rerender();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The version picker sits outside the loading cover, so a pick can arrive while
  // a lookup is in flight. Record it now, during render: the lookup callback can
  // resolve after this render and before the effect below, and it reads this ref.
  if (args.pickedVersion && args.pickedVersion !== seenPickRef.current) {
    seenPickRef.current = args.pickedVersion;
    stateRef.current = recordLanguageSwitchPick(stateRef.current, input, args.pickedVersion);
  }

  useEffect(() => {
    const current = inputRef.current;
    const { state: next, lookupId } = evaluateLanguageSwitch(stateRef.current, current);
    commit(next);
    if (lookupId == null) return;
    const pickAtLookup = seenPickRef.current;
    let cancelled = false;
    void lookupRef.current().then(
      (reports) => reports,
      () => null,
    ).then((reports) => {
      if (cancelled || !mountedRef.current || seenPickRef.current !== pickAtLookup) return;
      const { state: done, open } = finishLanguageSwitchLookup(
        stateRef.current,
        inputRef.current,
        lookupId,
        reports,
      );
      commit(done);
      if (open) onOpenRef.current(open);
    });
    return () => {
      // The next run of this effect (Strict Mode runs it twice, and enabled or
      // blocked changing does too) must be able to start another list fetch.
      cancelled = true;
      const abandoned = abandonLanguageSwitchLookup(stateRef.current, lookupId);
      if (abandoned !== stateRef.current) stateRef.current = abandoned;
    };
  }, [
    input.locale,
    input.caseId,
    input.enabled,
    input.blocked,
    input.loaded?.caseId,
    input.loaded?.chatKey,
    input.loaded?.epochTimeSecs,
    input.loaded?.responseLanguage,
    args.pickedVersion,
    commit,
  ]);

  // Rendered from the inputs of this render, so the cover and prompt never lag a commit.
  const view = languageSwitchView(evaluateLanguageSwitch(stateRef.current, input).state, input);
  const dismissPrompt = useCallback(() => {
    commit({ ...stateRef.current, prompt: null });
  }, [commit]);

  return { ...view, dismissPrompt };
}
